<script>
import { ref, watch, onMounted, computed, provide, getCurrentInstance, inject, nextTick } from 'vue';
import SmartLayoutC from '@rebuild/components/SmartLayoutC.vue';
import HeaderBar from './HeaderBar.vue';
import DiagensBanner from './DiagensBanner.vue';
import { useStoreCore } from '@/hooks';
import { useStore } from '@/utils/vue-api';
import { useNavigation } from '@rebuild/hooks/useNavigation';
import { refreshIframeDataKey } from '@rebuild/hooks/message';
import { useCoreData } from '@rebuild/hooks/data/useCoreData';
import { useFlowX } from '@rebuild/hooks/useFlowX';
import { useBusEvent } from '@rebuild/hooks/useBusEvent';

export default {
	name: 'FlowXLayoutWrapper',
	components: {
		SmartLayoutC
	},
	setup() {
		// Add font
		document.body.classList.add('is-diagens');

		const vm = getCurrentInstance();
		const store = useStore();

		const templateMenus = inject('TEMPLATE_MENUS');
		const getTemplateMenus = computed(() => templateMenus?.value || []);

		const { shouldShowUI } = useStoreCore();

		// ===================== 导航栏UI =====================
		const showMenuBar = computed(() => {
			if (!shouldShowUI('menuBar')) return false;
			const viewInDetailPage = vm.proxy.$route.params.subPath === 'detail';
			return !viewInDetailPage;
		});
		const getPageTmplData = computed(() => store.getters.getCommonData || {});
		const showAboveMenuBar = computed(() => {
			return showMenuBar.value && getPageTmplData.value.bannerConfig === 1;
		});
		const showBelowMenuBar = computed(() => {
			return showMenuBar.value && getPageTmplData.value.bannerConfig === 0;
		});

		// ===================== 导航栏Data =====================
		const activedMenuInfo = ref(null);
		provide('parentMenuActive', activedMenuInfo);
		const updateMenuActive = menu => {
			activedMenuInfo.value = menu;
		};

		const { findAnotherMenu } = useFlowX();

		const { goToHome, gotoMenu } = useNavigation();
		const changeActiveMenu = (menu, moduleIdx) => {
			updateMenuActive(menu);

			if (menu.isHome) {
				return goToHome();
			}

			const defaultModuleActionIdx = 0;
			const findModule = menu?.moduleVOS?.[moduleIdx || defaultModuleActionIdx];
			const findModuleId = findModule.moduleId;
			gotoMenu(menu.id, findModuleId);
		};
		const viewInHomePage = computed(() => {
			return checkHomeView(vm.proxy.$route);
		});
		const checkHomeView = route => {
			return route.name.includes('home');
		};
		const syncMenuActive = () => {
			const menus = getTemplateMenus.value;
			if (!menus.length) return;

			if (viewInHomePage.value) {
				const [homeMenu] = menus;
				updateMenuActive(homeMenu);
			} else {
				const menuId = vm.proxy.$route.params.menuId;
				if (!menuId) return;

				const findMenu = menus.find(menu => menu.id == menuId);
				const moduleId = vm.proxy.$route.params.moduleId;
				const anotherMenu = findAnotherMenu(findMenu, moduleId);

				const moduleVOS = anotherMenu?.moduleVOS || [];
				const findModule = moduleVOS.find(module => module.moduleId == moduleId);

				if (!anotherMenu || !findModule) {
					// 首页兜底
					updateMenuActive(menus[0]);
					replaceHome();
				} else {
					updateMenuActive(anotherMenu);
				}
			}
		};
		onMounted(() => {
			syncMenuActive();
		});
		// 切换路由时，检查一遍菜单选中状态
		watch(
			() => vm.proxy.$route,
			() => {
				syncMenuActive();
			}
		);

		const { setTemplateData } = useCoreData();
		const { replaceHome } = useNavigation();

		// 监听模板数据变化 更新视图
		useBusEvent('iframe-message', async msgData => {
			const { iframePreviewData, menuId, activeModuleId } = msgData;
			if (typeof iframePreviewData != 'undefined') {
				await handleIframeData(iframePreviewData);
			}
			if (menuId || activeModuleId) {
				handleActiveMenuId(menuId, activeModuleId);
			}
		});
		const handleIframeData = async data => {
			// 拿到模板数据, 更新本地数据并重新写入
			setTemplateData(refreshIframeDataKey(data));
			await nextTick();
			syncMenuActive();
		};
		const handleActiveMenuId = (menuId, moduleId) => {
			const menus = getTemplateMenus.value;
			const menuInfo = menus.find(menu => menu.__id == menuId);
			if (!menuInfo) return;
			if (viewInHomePage.value && menuInfo.isHome) return;

			const moduleVOS = menuInfo?.moduleVOS || [];
			const moduleIdx = moduleId ? moduleVOS.findIndex(module => module.moduleId == moduleId) : 0;
			randomKey.value = Math.random();

			changeActiveMenu(menuInfo, moduleIdx);
		};
		const randomKey = ref(Math.random());

		return {
			showAboveMenuBar,
			showBelowMenuBar,
			changeActiveMenu,
			randomKey
		};
	},
	render() {
		const mainRenderKey = `${this.$route.fullPath}_${this.randomKey}`;

		const AboveMenuBar = (
			<SmartLayoutC
				compId="layout_nav"
				mode="basic"
				key="layout_nav"
				styleType="above"
				onChange={this.changeActiveMenu}
			/>
		);
		const BelowMenuBar = (
			<SmartLayoutC
				compId="layout_nav"
				mode="basic"
				key="layout_nav"
				styleType="below"
				onChange={this.changeActiveMenu}
			/>
		);

		const Main = <router-view key={mainRenderKey} />;
		const Footer = <SmartLayoutC compId="layout_footer" mode="basic" key="layout_footer" />;
		const Floating = <SmartLayoutC compId="layout_floating" mode="basic" key="layout_floating" />;

		const Header = <HeaderBar />;
		const Banner = <DiagensBanner />;

		let layouts = [];
		const initRender = () => {
			if (this.showAboveMenuBar) {
				layouts = [Header, AboveMenuBar, Banner, Main, Footer, Floating];
			} else if (this.showBelowMenuBar) {
				layouts = [Header, Banner, BelowMenuBar, Main, Footer, Floating];
			} else {
				layouts = [Header, Banner, Main, Footer, Floating];
			}
		};
		initRender();

		return <div class="flowX-layout ex2rem-min-w1200-layout-next">{...layouts}</div>;
	}
};
</script>

<style lang="less" scoped></style>
